import { api } from "./api";
import { API_ENDPOINTS } from "../../utils/api-service";
import {
  toNullableStr,
  toNumberOr,
  toNumberOrNull,
  toStr,
  unwrapApiData,
} from "./points-service-helpers";
import {
  AdminLedgerEntry,
  CreatePointAdjustmentPayload,
  CreatedPointAdjustment,
  PointAdjustmentUserOption,
  PointLedgerFilters,
  PointLedgerPage,
  PointLedgerQuery,
} from "../../models/pointLedger";

const asRecord = (value: unknown): Record<string, unknown> =>
  (typeof value === "object" && value !== null ? value : {}) as Record<string, unknown>;

/** Distingue "objeto anidado" de `null`, que acá es información: no hay regla / no hay entidad. */
const asRecordOrNull = (value: unknown): Record<string, unknown> | null =>
  typeof value === "object" && value !== null ? (value as Record<string, unknown>) : null;

/** El id de la entidad relacionada es texto polimórfico; un número también se acepta. */
const toIdStr = (value: unknown): string | null => {
  if (typeof value === "number" && Number.isFinite(value)) return String(value);
  return toNullableStr(value);
};

const mapRule = (raw: unknown): AdminLedgerEntry["rule"] => {
  const rule = asRecordOrNull(raw);
  const id = rule === null ? null : toNumberOrNull(rule.id);
  // Sin id no hay regla que mostrar: mejor "—" que una regla fantasma con id 0.
  return rule === null || id === null ? null : { id, title: toNullableStr(rule.title) };
};

const mapRelated = (raw: unknown): AdminLedgerEntry["related"] => {
  const related = asRecordOrNull(raw);
  if (related === null) return null;
  const type = toNullableStr(related.type);
  const id = toIdStr(related.id);
  return type === null || id === null ? null : { type, id, label: toNullableStr(related.label) };
};

/**
 * Los motivos del ajuste manual. Los cuatro datos vienen juntos o no viene nada: el backend
 * manda el objeto entero sólo para los movimientos que cargó una persona.
 */
const mapAdjustment = (raw: unknown): AdminLedgerEntry["adjustment"] => {
  const adjustment = asRecordOrNull(raw);
  if (adjustment === null) return null;
  const userReason = toNullableStr(adjustment.user_reason);
  // Sin el texto del usuario no hay ajuste que mostrar: en la base es NOT NULL, así que su
  // ausencia significa que la fila no es un ajuste manual.
  if (userReason === null) return null;

  const createdBy = asRecord(adjustment.created_by);
  return {
    internalReason: toStr(adjustment.internal_reason),
    userReason,
    createdBy: { id: toNumberOr(createdBy.id, 0), fullName: toStr(createdBy.full_name) },
  };
};

export const mapCreatedPointAdjustment = (response: unknown): CreatedPointAdjustment => {
  const row = asRecord(unwrapApiData(response));
  const user = asRecord(row.user);

  return {
    ledgerId: toNumberOr(row.ledger_id, 0),
    tenantId: toStr(row.tenant_id),
    user: {
      id: toNumberOr(user.id, 0),
      fullName: toStr(user.full_name),
      email: toNullableStr(user.email),
    },
    points: toNumberOr(row.points, 0),
    balanceAfter: toNumberOr(row.balance_after, 0),
    internalReason: toStr(row.internal_reason),
    userReason: toStr(row.user_reason),
    createdAt: toStr(row.created_at),
  };
};

/**
 * Las opciones del buscador de personas.
 *
 * SE DESCARTA LA QUE NO TRAE MAIL: el formulario identifica a la persona por mail, así que una
 * opción sin mail no se puede elegir — elegirla dejaría el campo vacío sin explicar por qué.
 */
export const mapPointAdjustmentUserOptions = (response: unknown): PointAdjustmentUserOption[] => {
  const rows = unwrapApiData(response);
  if (!Array.isArray(rows)) return [];

  return rows.reduce<PointAdjustmentUserOption[]>((options, raw: unknown) => {
    const row = asRecord(raw);
    const email = toNullableStr(row.email);
    if (email === null) return options;

    options.push({
      id: toNumberOr(row.id, 0),
      fullName: toStr(row.full_name),
      email,
      balance: toNumberOr(row.balance, 0),
    });
    return options;
  }, []);
};

export const mapAdminLedgerEntry = (raw: unknown): AdminLedgerEntry => {
  const row = asRecord(raw);
  const user = asRecord(row.user);

  return {
    id: toNumberOr(row.id, 0),
    tenantId: toStr(row.tenant_id),
    createdAt: toStr(row.created_at),
    user: {
      id: toNumberOr(user.id, 0),
      fullName: toStr(user.full_name),
      email: toNullableStr(user.email),
    },
    actionType: toStr(row.action_type),
    points: toNumberOr(row.points, 0),
    rule: mapRule(row.rule),
    related: mapRelated(row.related),
    adjustment: mapAdjustment(row.adjustment),
    // `is_reversed` está en el contrato y es booleano: cualquier otra cosa es un movimiento vigente.
    isReversed: row.is_reversed === true,
  };
};

/**
 * La paginación del ledger viene PLANA (`items`, `total`, `page`, `limit`) y no dentro de
 * `pagination` como en canjes: es el contrato del endpoint, no un descuido.
 */
export const mapPointLedgerPage = (response: unknown): PointLedgerPage => {
  const data = asRecord(unwrapApiData(response));
  const items = Array.isArray(data.items)
    ? data.items.map((row: unknown) => mapAdminLedgerEntry(row))
    : [];

  return {
    items,
    total: toNumberOr(data.total, items.length),
    page: toNumberOr(data.page, 1),
    limit: toNumberOr(data.limit, items.length),
  };
};

/**
 * Filtros → query params. Sin `tenant_id` el backend devuelve todas las instituciones (y al
 * admin de institución lo fuerza a la suya igual). `sign` viaja siempre, incluso cuando vale
 * el default del backend: así el request dice qué está mirando el admin.
 *
 * El `email` se recorta acá, al armar el param, y no en el estado del input: así lo que se
 * escribe queda tal cual mientras se escribe. Un mail que queda vacío después del `trim()` NO
 * viaja —el backend responde 400 a `?email=`, y además un filtro vacío no filtra nada—. La
 * capitalización se manda tal cual: el match del backend es case-insensitive.
 */
export const toPointLedgerParams = (
  filters: PointLedgerFilters
): Record<string, string | number> => {
  const email: string = filters.email?.trim() ?? "";

  return {
    ...(filters.tenantId !== null ? { tenant_id: filters.tenantId } : {}),
    sign: filters.sign,
    ...(filters.actionType !== null ? { action_type: filters.actionType } : {}),
    ...(filters.userId !== null && filters.userId !== undefined
      ? { user_id: filters.userId }
      : {}),
    ...(email !== "" ? { email } : {}),
    ...(filters.from !== null ? { from: filters.from } : {}),
    ...(filters.to !== null ? { to: filters.to } : {}),
  };
};

export const pointLedgerApi = api.injectEndpoints({
  endpoints: (build) => ({
    getPointLedger: build.query<PointLedgerPage, PointLedgerQuery>({
      query: ({ page, limit, ...filters }) => ({
        url: API_ENDPOINTS.POINTS.LEDGER,
        params: { ...toPointLedgerParams(filters), page, limit },
      }),
      transformResponse: (response: unknown) => mapPointLedgerPage(response),
      providesTags: (_result, _error, { tenantId }) => [
        { type: "pointLedger" as const, id: tenantId ?? "ALL" },
      ],
    }),

    /**
     * El desplegable de personas del alta.
     *
     * SIN `providesTags`: es un typeahead que se consulta por texto y se descarta al cerrar el
     * formulario. Cachearlo por etiqueta significaría invalidarlo con cada ajuste para
     * refrescar un saldo que, de todos modos, el backend vuelve a leer al escribir.
     */
    searchPointAdjustmentUsers: build.query<
      PointAdjustmentUserOption[],
      { search: string; tenantId: string | null }
    >({
      query: ({ search, tenantId }) => ({
        url: API_ENDPOINTS.POINTS.LEDGER_ADJUSTMENT_USERS,
        params: { search, ...(tenantId !== null ? { tenant_id: tenantId } : {}) },
      }),
      transformResponse: (response: unknown) => mapPointAdjustmentUserOptions(response),
    }),

    /**
     * Carga un movimiento a mano. El signo viaja en `points`.
     *
     * INVALIDA LAS DOS ETIQUETAS: la de la institución del ajuste y la de "todas". Un
     * SUPER_ADMIN que carga un ajuste desde el scope global tiene en pantalla la lista de
     * TODAS, así que invalidar sólo la del tenant dejaría la grilla sin la fila que acaba de
     * crear — y el primer reflejo ante eso es volver a cargarlo.
     */
    createPointAdjustment: build.mutation<CreatedPointAdjustment, CreatePointAdjustmentPayload>({
      query: ({ tenantId, email, points, internalReason, userReason }) => ({
        url: API_ENDPOINTS.POINTS.LEDGER_ADJUSTMENTS,
        method: "POST",
        body: {
          ...(tenantId !== null ? { tenant_id: tenantId } : {}),
          email,
          points,
          internal_reason: internalReason,
          user_reason: userReason,
        },
      }),
      transformResponse: (response: unknown) => mapCreatedPointAdjustment(response),
      invalidatesTags: (_result, _error, { tenantId }) => [
        { type: "pointLedger" as const, id: tenantId ?? "ALL" },
        { type: "pointLedger" as const, id: "ALL" },
      ],
    }),
  }),
});

export const {
  useGetPointLedgerQuery,
  useCreatePointAdjustmentMutation,
  useLazySearchPointAdjustmentUsersQuery,
} = pointLedgerApi;
